import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CircleHelp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useIntegrationProbe } from '@/lib/providers/use-providers';
import { effectiveMode } from '@/lib/providers/binding-mode';
import {
  canonicalCategory,
  integrationKey,
  rowHealth,
  vendorLabel,
  type UnknownEntry,
} from '@/lib/providers/integration-state';
import { credentialsMissing, type VendorAccount } from '@/lib/providers/providers-overview';
import { AIRLINE_PROVIDER_VENDOR } from '@/lib/providers/airline-provider';
import type { IntegrationDescriptor, IntegrationHealth, RoutingMember } from '@/lib/providers/types';
import { IntegrationProbe } from './integrations-probe';
import { BootModeForbiddenNotice, IntegrationModeFacts, IntegrationModeNote, ModeCell } from './integration-mode';
import { IntegrationSwitch, IntegrationToggleNote } from './integration-toggle';
import { AirlineProviderLink } from './airline-provider-card';
import {
  DetailItem,
  DetailLayout,
  InUseNote,
  ModeChip,
  ProviderRow,
  StatusCell,
  type StatusTone,
} from './provider-table';
import { capabilitiesText, formatWhen, relativeTime } from './format';

/**
 * THE ROWS of a service card — and, separately, one declarant we could not reach.
 *
 *  · {@link IntegrationRow} — an integration this instance built (or can switch
 *    back on): mode chip, availability, what it is used for, the on/off switch;
 *    its detail holds the endpoint, credentials, the manual connection test and
 *    the "Order of use" controls.
 *  · {@link UnwiredChannelRow} — a CHANNEL this instance does not offer ("no
 *    WhatsApp here"). A missing reach is a fact about the instance, so it is a
 *    named row, dimmed, never folded into the "also available" line.
 *  · {@link MemberRow} — a routed provider the inventory gave no row of its own,
 *    so its order controls still have a place.
 *  · {@link UnknownDeclarantCard} — "we could not ask this app". A POSITIVE
 *    RECORD OF IGNORANCE, kept structurally apart from every row above: it has no
 *    descriptor, so no `!enabled` test can ever reach it, and it never counts as
 *    "off or not configured".
 *
 * ── THE STATUS CELL IS THE POINT OF THE ROW (#840) ───────────────────────────
 * One answer to "is it available?": Online · Offline · Checking… · Available
 * (synthetic, answering inside the platform) · No direct check · Missing
 * credentials · Off — and how it is known, in a few words. The operator-bff
 * computes it on the read (`health`); "Test connection" is the manual re-check
 * and its answer shows here at once. Synthetic is AVAILABLE — violet, never a
 * failure — and the mode chip beside it says SYNTHETIC, so a box running on
 * synthetic providers still cannot be mistaken for one talking to real ones.
 */

const HEALTH_TONE: Record<IntegrationHealth['state'], StatusTone> = {
  online: 'ok',
  offline: 'fail',
  checking: 'checking',
  simulated: 'synthetic',
  unverifiable: 'neutral',
  no_credentials: 'warn',
  disabled: 'off',
};

/** The status cell's short hint: how the state is known, and when. */
function healthHint(
  t: TFunction<'network'>,
  health: IntegrationHealth,
  descriptor: IntegrationDescriptor,
  key: string,
  locale: string,
): ReactNode {
  const when = health.checkedAt ? relativeTime(health.checkedAt, t) : '';
  const checked = (text: string) => (
    <span data-testid={`integration-health-checked-${key}`}>{text}</span>
  );
  switch (health.state) {
    case 'online':
      if (!when) return t('providers.rowStatus.hint.online');
      return checked(
        health.source === 'traffic'
          ? t('providers.rowStatus.hint.usedAgo', { when })
          : t('providers.rowStatus.hint.answeredAgo', { when }),
      );
    case 'offline':
      if (health.detail) {
        return (
          <span className="text-rose-700 dark:text-rose-400" data-testid={`integration-health-detail-${key}`}>
            {t('providers.integrations.health.failedStep', { detail: health.detail })}
          </span>
        );
      }
      return (
        <>
          {health.source === 'traffic'
            ? t('providers.rowStatus.hint.offlineTraffic')
            : t('providers.rowStatus.hint.offline')}
          {when ? <> · {checked(t('providers.rowStatus.hint.checkedAgo', { when }))}</> : null}
        </>
      );
    case 'disabled': {
      const last = descriptor.toggle?.lastChange;
      if (last && !last.enabled) {
        return t('providers.rowStatus.hint.disabledBy', {
          who: last.byName ?? last.byUrn,
          when: formatWhen(last.at, locale),
        });
      }
      return t('providers.rowStatus.hint.disabled');
    }
    default:
      return t(`providers.rowStatus.hint.${health.state}`);
  }
}

/** The long sentence of the detail: what the state means. */
function healthSentence(t: TFunction<'network'>, health: IntegrationHealth): string {
  if (health.state === 'online' && health.source === 'traffic') {
    return t('providers.integrations.health.hint.onlineTraffic');
  }
  if (health.state === 'offline' && health.source === 'traffic' && !health.detail) {
    return t('providers.integrations.health.hint.offlineTraffic');
  }
  return t(`providers.integrations.health.hint.${health.state}`);
}

/** The services a shared account provides besides this row's, in the operator's words. */
function otherServices(t: TFunction<'network'>, account: VendorAccount, category: string): string {
  const own = canonicalCategory(category);
  return account.services
    .filter((s) => s !== own)
    .map((s) => t(`providers.integrations.category.${s}`, { defaultValue: s }))
    .join(' · ');
}

/** The muted second line of a row: the declaring service, and "shared account" when it is one. */
function rowSub(t: TFunction<'network'>, descriptor: IntegrationDescriptor, account: VendorAccount | undefined) {
  return account ? t('providers.account.shared', { declaredBy: descriptor.declaredBy }) : descriptor.declaredBy;
}

/**
 * Shared facts of a descriptor, for both the wired and the unwired row. A
 * binding of a SHARED account (D1: one vendor, several services) does not
 * repeat the account's details — it points at the account line instead.
 */
function DescriptorFacts({
  descriptor,
  k,
  account,
}: {
  descriptor: IntegrationDescriptor;
  k: string;
  account?: VendorAccount | undefined;
}) {
  const { t } = useTranslation('network');
  const notNeeded = effectiveMode(descriptor) === 'mock' || descriptor.authStrategy === 'none';
  const missing = credentialsMissing(descriptor);
  return (
    <>
      {descriptor.baseUrl ? (
        <DetailItem label={t('providers.integrations.endpoint')} className="font-mono">
          {descriptor.baseUrl}
        </DetailItem>
      ) : null}
      {account ? (
        <DetailItem label={t('providers.account.label')} testId={`integration-account-${k}`}>
          {t('providers.account.ref', {
            name: vendorLabel(t, descriptor.category, descriptor.vendor),
            services: otherServices(t, account, descriptor.category),
          })}
        </DetailItem>
      ) : (
        <AccountFacts descriptor={descriptor} k={k} missing={missing} notNeeded={notNeeded} />
      )}
      <DetailItem label={t('providers.detail.declaredBy')} className="font-mono">
        {descriptor.declaredBy}
      </DetailItem>
      {descriptor.capabilities?.length ? (
        <DetailItem label={t('providers.integrations.capabilities')}>
          {capabilitiesText(t, descriptor.capabilities)}
        </DetailItem>
      ) : null}
      {descriptor.timeoutMs ? (
        <DetailItem label={t('providers.detail.timeout')}>
          {t('providers.detail.timeoutValue', { ms: descriptor.timeoutMs })}
        </DetailItem>
      ) : null}
    </>
  );
}

/** Authentication + credentials of a vendor that serves this one service only. */
function AccountFacts({
  descriptor,
  k,
  missing,
  notNeeded,
}: {
  descriptor: IntegrationDescriptor;
  k: string;
  missing: boolean;
  notNeeded: boolean;
}) {
  const { t } = useTranslation('network');
  return (
    <>
      <DetailItem label={t('providers.integrations.authLabel')}>
        {t(`providers.integrations.auth.${descriptor.authStrategy}`, { defaultValue: descriptor.authStrategy })}
      </DetailItem>
      <DetailItem
        label={t('providers.integrations.credentials.label')}
        testId={`integration-credentials-${k}`}
        className={
          descriptor.credentialsPresent
            ? 'text-emerald-700 dark:text-emerald-400'
            : missing
              ? 'font-semibold text-amber-700 dark:text-amber-400'
              : undefined
        }
      >
        {descriptor.credentialsPresent
          ? t('providers.integrations.credentials.present')
          : missing
            ? t('providers.integrations.credentials.missing')
            : notNeeded
              ? t('providers.integrations.credentials.notNeeded')
              : t('providers.integrations.credentials.absent')}
      </DetailItem>
    </>
  );
}

interface IntegrationRowProps {
  descriptor: IntegrationDescriptor;
  /** Its place in the card's "Order of use", when routed. */
  position?: number | undefined;
  /** Its place is fixed (the airline always wins) — the why. */
  locked?: string | undefined;
  /** The configured egress limit, when the recent-traffic read reports one. */
  rateLimit?: string | undefined;
  /** The "Order of use" controls for this vendor (row detail). */
  routing?: ReactNode;
  hidden?: boolean | undefined;
  /** The vendor's account, when it serves several services (D1). */
  account?: VendorAccount | undefined;
}

/**
 * A wired integration: this instance built it, and traffic in this category
 * goes through it (or it can be switched back on).
 */
export function IntegrationRow({ descriptor, position, locked, rateLimit, routing, hidden, account }: IntegrationRowProps) {
  const { t, i18n } = useTranslation('network');
  const key = integrationKey(descriptor);
  // ONE probe state per row: the status cell and the step list read the same
  // result, so the cell can never contradict a rendered outcome — and it lives
  // on the row, so a closed detail keeps its last result.
  const probe = useIntegrationProbe(descriptor.vendor);
  const outcome = probe.data;
  const health = rowHealth(descriptor, outcome?.kind === 'result' ? outcome.result : undefined);
  const when = health.checkedAt ? relativeTime(health.checkedAt, t) : '';

  return (
    <ProviderRow
      testId={`integration-row-${key}`}
      dataState={health.state}
      dataHealthSource={health.source}
      dataMode={effectiveMode(descriptor)}
      hidden={hidden}
      dimmed={!descriptor.enabled}
      position={position}
      locked={locked}
      name={vendorLabel(t, descriptor.category, descriptor.vendor)}
      nameTitle={descriptor.vendor}
      sub={rowSub(t, descriptor, account)}
      expandTestId={`integration-expand-${key}`}
      mode={<ModeCell descriptor={descriptor} />}
      status={
        <StatusCell
          tone={HEALTH_TONE[health.state]}
          label={t(`providers.rowStatus.label.${health.state}`)}
          hint={healthHint(t, health, descriptor, key, i18n.language)}
          state={health.state}
          labelTestId={`integration-health-${key}`}
          hintTestId={`integration-health-hint-${key}`}
        />
      }
      usedFor={capabilitiesText(t, descriptor.capabilities)}
      inUse={
        // The airline provider is switched on its own card (one provider, three services):
        // its rows here say on/off and point there instead of carrying a second switch.
        descriptor.vendor === AIRLINE_PROVIDER_VENDOR ? (
          <AirlineProviderLink descriptor={descriptor} />
        ) : (
          <IntegrationSwitch descriptor={descriptor} />
        )
      }
      notice={descriptor.bootModeForbidden ? <BootModeForbiddenNotice descriptor={descriptor} /> : undefined}
      detailTestId={`integration-detail-${key}`}
      renderDetail={({ nameId }) => (
        <DetailLayout
          facts={
            <>
              <DetailItem label={t('providers.table.status')}>{healthSentence(t, health)}</DetailItem>
              {when ? (
                <DetailItem
                  label={health.source === 'traffic' ? t('providers.detail.lastUsed') : t('providers.detail.checked')}
                >
                  {when}
                </DetailItem>
              ) : null}
              <IntegrationModeFacts descriptor={descriptor} />
              <DescriptorFacts descriptor={descriptor} k={key} account={account} />
              {rateLimit ? (
                <DetailItem label={t('providers.card.rateLimit')} className="font-mono" testId={`integration-ratelimit-${key}`}>
                  {rateLimit}
                </DetailItem>
              ) : null}
            </>
          }
          actions={
            <>
              <IntegrationProbe
                vendor={descriptor.vendor}
                testId={key}
                outcome={outcome}
                isPending={probe.isPending}
                isError={probe.isError}
                onRun={() => probe.mutate()}
                labelledBy={nameId}
              />
              {routing}
              <IntegrationModeNote descriptor={descriptor} />
              <IntegrationToggleNote descriptor={descriptor} />
            </>
          }
        />
      )}
    />
  );
}

/**
 * A named channel this instance CANNOT USE — only ever a row in a `channels`
 * category, where an entry is an independent reach rather than an alternative
 * supplier. It reads "Not enabled", dimmed, and is never collapsed away.
 */
export function UnwiredChannelRow({
  descriptor,
  position,
  locked,
  routing,
  hidden,
  account,
}: {
  descriptor: IntegrationDescriptor;
  position?: number | undefined;
  locked?: string | undefined;
  routing?: ReactNode;
  hidden?: boolean | undefined;
  account?: VendorAccount | undefined;
}) {
  const { t } = useTranslation('network');
  const key = integrationKey(descriptor);
  return (
    <ProviderRow
      testId={`integration-unwired-${key}`}
      dataState="not-configured"
      hidden={hidden}
      dimmed
      position={position}
      locked={locked}
      name={vendorLabel(t, descriptor.category, descriptor.vendor)}
      nameTitle={descriptor.vendor}
      sub={rowSub(t, descriptor, account)}
      expandTestId={`integration-expand-${key}`}
      mode={<ModeChip mode={null} />}
      status={
        <StatusCell
          tone="neutral"
          label={t('providers.rowStatus.label.not_enabled')}
          hint={t('providers.rowStatus.hint.not_enabled')}
        />
      }
      usedFor={capabilitiesText(t, descriptor.capabilities)}
      inUse={<InUseNote>{t('providers.inUseCell.notEnabled')}</InUseNote>}
      detailTestId={`integration-detail-${key}`}
      renderDetail={() => (
        <DetailLayout
          facts={<DescriptorFacts descriptor={descriptor} k={key} account={account} />}
          actions={routing ?? undefined}
        />
      )}
    />
  );
}

/** A routed provider with no inventory row of its own: name, mode, serving, order. */
export function MemberRow({
  category,
  member,
  position,
  locked,
  routing,
  hidden,
}: {
  category: string;
  member: RoutingMember;
  position?: number | undefined;
  locked?: string | undefined;
  routing?: ReactNode;
  hidden?: boolean | undefined;
}) {
  const { t } = useTranslation('network');
  const key = integrationKey({ category, vendor: member.vendor });
  return (
    <ProviderRow
      testId={`integration-member-${key}`}
      dataState={member.serving ? 'serving' : 'not-serving'}
      dataMode={member.mode}
      hidden={hidden}
      dimmed={!member.serving}
      position={position}
      locked={locked}
      name={vendorLabel(t, category, member.vendor)}
      nameTitle={member.vendor}
      expandTestId={`integration-expand-${key}`}
      mode={<ModeChip mode={member.mode ?? null} category={category} />}
      status={
        member.serving ? (
          <StatusCell tone="neutral" label={t('providers.rowStatus.label.serving')} />
        ) : (
          <StatusCell tone="off" label={t('providers.rowStatus.label.disabled')} hint={t('providers.routing.notServing')} />
        )
      }
      usedFor="—"
      inUse={<InUseNote>—</InUseNote>}
      detailTestId={`integration-detail-${key}`}
      renderDetail={() => <DetailLayout facts={null} actions={routing ?? undefined} />}
    />
  );
}

/**
 * "We could not ask this app." It sits ABOVE every service card, because we do
 * not even know which service its integrations belong to, and because it is the
 * only thing here that might be an outage. Dashed, amber, question-marked, and
 * it says so in a sentence, because a colour alone is not an argument.
 */
export function UnknownDeclarantCard({ entry }: { entry: UnknownEntry }) {
  const { t } = useTranslation('network');
  return (
    <div
      className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-dashed border-amber-500/60 bg-amber-500/5 p-3"
      data-testid={`integration-unknown-${entry.declaredBy}`}
      data-state="unknown"
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-2 font-medium">
          <CircleHelp className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <span className="truncate font-mono text-sm" title={entry.declaredBy}>
            {entry.declaredBy}
          </span>
        </h3>
        <Badge
          variant="warning"
          className="shrink-0 font-mono uppercase tracking-wide"
          data-testid={`integration-unknown-badge-${entry.declaredBy}`}
        >
          {t('providers.integrations.unknown.badge')}
        </Badge>
      </div>
      <p className="text-xs text-amber-800 dark:text-amber-300">
        {t('providers.integrations.unknown.body', { service: entry.declaredBy })}{' '}
        <span className="text-muted-foreground">{t('providers.integrations.unknown.notTheSame')}</span>
      </p>
      <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
        {entry.reason ? (
          <div className="flex gap-1.5">
            <dt>{t('providers.integrations.unknown.reasonLabel')}</dt>
            <dd className="text-foreground">
              {t(`providers.integrations.unknown.reason.${entry.reason}`, { defaultValue: entry.reason })}
            </dd>
          </div>
        ) : null}
        {entry.detail ? (
          <div className="flex min-w-0 gap-1.5">
            <dt>{t('providers.integrations.unknown.detailLabel')}</dt>
            <dd className="min-w-0 truncate font-mono" title={entry.detail}>
              {entry.detail}
            </dd>
          </div>
        ) : null}
        {entry.observedAt ? (
          <div className="flex gap-1.5">
            <dt>{t('providers.integrations.unknown.checkedLabel')}</dt>
            <dd>{relativeTime(entry.observedAt, t)}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
